/**
 * Recherche de matricule par nom + date naissance.
 */
import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { PortailLayout } from '../PortailLayout';
import { portailApi, MatriculeResult } from '../services/portailApi';

export default function FindMatricule() {
  const navigate = useNavigate();
  const [nom, setNom] = useState('');
  const [prenoms, setPrenoms] = useState('');
  const [dateNaissance, setDateNaissance] = useState('');
  const [results, setResults] = useState<MatriculeResult[] | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    setResults(null);
    try {
      const res = await portailApi.findMatricule({
        nom: nom.trim(),
        prenoms: prenoms.trim() || undefined,
        date_naissance: dateNaissance,
      });
      setResults(res.results);
    } catch (e: any) {
      setError(e.message || 'Erreur lors de la recherche');
    } finally {
      setLoading(false);
    }
  };

  const choose = (mat: MatriculeResult) => {
    // Stocker la pré-sélection en sessionStorage et rediriger vers première-connexion
    sessionStorage.setItem('portail_prefill_matricule', mat.matricule);
    sessionStorage.setItem('portail_prefill_date', dateNaissance);
    navigate('/portail/premiere-connexion');
  };

  return (
    <PortailLayout showHeader title="Retrouver mon matricule">
      <div className="space-y-4 mt-4">
        <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4 text-sm text-blue-800 dark:text-blue-200">
          Renseignez votre nom et votre date de naissance pour retrouver votre matricule.
        </div>

        <form onSubmit={submit} className="bg-gray-900 rounded-2xl shadow p-5 space-y-3 border border-gray-800">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Nom *
            </label>
            <input
              type="text"
              value={nom}
              onChange={(e) => setNom(e.target.value)}
              required
              autoCapitalize="characters"
              className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Prénom(s) (optionnel)
            </label>
            <input
              type="text"
              value={prenoms}
              onChange={(e) => setPrenoms(e.target.value)}
              className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Date de naissance *
            </label>
            <input
              type="date"
              value={dateNaissance}
              onChange={(e) => setDateNaissance(e.target.value)}
              required
              className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
            />
          </div>

          {error && (
            <div className="p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg text-sm text-red-700 dark:text-red-300">
              {error}
            </div>
          )}

          <button
            type="submit"
            disabled={loading}
            className="w-full bg-blue-600 hover:bg-blue-700 text-white font-semibold py-3 rounded-lg disabled:opacity-50"
          >
            {loading ? 'Recherche...' : 'Rechercher'}
          </button>
        </form>

        {results && results.length > 0 && (
          <div className="bg-gray-900 rounded-2xl shadow p-5 border border-gray-800">
            <h3 className="font-semibold text-gray-900 dark:text-white mb-3">
              Résultats trouvés
            </h3>
            <div className="space-y-2">
              {results.map((r) => (
                <button
                  key={r.matricule}
                  onClick={() => choose(r)}
                  className="w-full text-left p-3 border border-gray-700 rounded-lg hover:bg-blue-50 dark:hover:bg-blue-900/20 transition-colors"
                >
                  <div className="flex justify-between items-center">
                    <div>
                      <div className="font-medium text-gray-900 dark:text-white">
                        {r.nom} {r.prenoms}
                      </div>
                      <div className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                        Classe : {r.classe || '—'}
                      </div>
                    </div>
                    <div className="font-mono text-sm text-blue-600 dark:text-blue-400">
                      {r.matricule_masque}
                    </div>
                  </div>
                </button>
              ))}
            </div>
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-3">
              Cliquez sur votre nom pour continuer. Le matricule complet vous sera révélé après confirmation de votre date de naissance.
            </p>
          </div>
        )}

        <Link
          to="/portail/login"
          className="block text-center text-sm text-blue-600 dark:text-blue-400 hover:underline mt-4"
        >
          ← Retour à la connexion
        </Link>
      </div>
    </PortailLayout>
  );
}
